/**
 * Route Map — a subdomain's Owl "decisive" routing report: which CDN platform /
 * routing strategy serves each geo + network. Read-only, paginated, and filtered
 * by the shared 4-level geo picker (Country / Region / City / Network).
 * Subdomain-scoped via the shared scope bar.
 */
import { useEffect, useMemo, useState } from 'react';
import { Waypoints, RotateCcw } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useWafRoutingReport,
  useWafPlatforms,
  type WafRoutingEntry,
} from '@/api/waf';
import { countryName, regionName, cityName, networkName } from '../_shared/geo';
import { WafGeoFilter, geoFilterToApi } from '../_shared/WafGeoFilter';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafRouteMap.module.css';

const PAGE_SIZE = 20;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

/** Vendor convention: "00" or empty means "any/unspecified" for a geo dimension. */
const isAny = (code: string) => !code || code === '00';

/** The filter bar + report table for one resolved `{ serviceId, domain }`. */
function RouteMapBody({ serviceId, domain }: { serviceId: string; domain: string }) {
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [network, setNetwork] = useState('');
  const [page, setPage] = useState(1);

  const apiFilter = useMemo(
    () => geoFilterToApi(country, region, city, network),
    [country, region, city, network],
  );

  // Any filter change resets pagination to the first page.
  useEffect(() => {
    setPage(1);
  }, [apiFilter.country, apiFilter.region, apiFilter.city, apiFilter.network]);

  const reset = () => {
    setCountry('');
    setRegion('');
    setCity('');
    setNetwork('');
  };

  const reportQ = useWafRoutingReport(serviceId, domain, apiFilter, page, PAGE_SIZE);
  const platformsQ = useWafPlatforms(serviceId);

  const platformLabel = useMemo(() => {
    const m = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name]));
    return (id: string) => (id === '0' ? 'Origin' : m.get(id) ?? id);
  }, [platformsQ.data]);

  const entries = reportQ.data?.items ?? [];
  const pagination = reportQ.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? entries.length;

  const columns = useMemo<ColumnDef<WafRoutingEntry, unknown>[]>(
    () => [
      {
        id: 'location',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }) => {
          const { country, region, city } = row.original;
          if (isAny(country)) return muted('Global');
          const sub = [
            !isAny(region) ? regionName(country, region) : null,
            !isAny(city) ? cityName(country, city) : null,
          ].filter(Boolean);
          return (
            <div style={{ display: 'grid', lineHeight: 1.3 }}>
              <span style={{ fontWeight: 500 }}>{countryName(country)}</span>
              {sub.length > 0 && (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  {sub.join(' › ')}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'network',
        header: 'Network',
        enableSorting: false,
        cell: ({ row }) =>
          !isAny(row.original.network)
            ? networkName(row.original.country, row.original.network)
            : muted('Any'),
      },
      {
        id: 'cdn',
        header: 'Assigned CDN',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontWeight: 500 }}>{platformLabel(row.original.platform_id)}</span>
        ),
      },
      {
        id: 'strategy',
        header: 'Current Strategy',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.strategy_name ? (
            <Badge tone="info" appearance="subtle" size="sm">
              {row.original.strategy_name}
            </Badge>
          ) : (
            <Badge tone="neutral" appearance="subtle" size="sm">
              Default
            </Badge>
          ),
      },
    ],
    [platformLabel],
  );

  const hasFilter = !!(country || region || city || network);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div className={styles.filterRow}>
        <WafGeoFilter
          country={country}
          region={region}
          city={city}
          network={network}
          setCountry={setCountry}
          setRegion={setRegion}
          setCity={setCity}
          setNetwork={setNetwork}
          disabled={reportQ.isFetching}
        />
        <div className={styles.filterActions}>
          {hasFilter && (
            <Button variant="ghost" onClick={reset} disabled={reportQ.isFetching}>
              Clear filters
            </Button>
          )}
          <Button
            variant="secondary"
            startIcon={<RotateCcw size={15} />}
            onClick={() => reportQ.refetch()}
            disabled={reportQ.isFetching}
            aria-label="Refresh route map"
          >
            Refresh
          </Button>
        </div>
      </div>

      {reportQ.isPending ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : reportQ.isError ? (
        <Alert
          tone="danger"
          title="Couldn't load the route map"
          actions={<button onClick={() => reportQ.refetch()}>Retry</button>}
        >
          The WAF service couldn't return this subdomain's routing report. Try again.
        </Alert>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={entries}
            getRowId={(r, i) => `${r.platform_id}:${r.country}:${r.region}:${r.city}:${r.network}:${i}`}
            emptyState={
              <EmptyState
                icon={<Waypoints size={20} />}
                title="No routing entries"
                description={
                  hasFilter
                    ? 'No routing decisions match the selected filters.'
                    : 'This subdomain has no routing decisions to show yet.'
                }
              />
            }
            footer={total ? `Showing ${entries.length} of ${total} entries` : undefined}
          />
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                page={page}
                pageCount={totalPages}
                onPageChange={setPage}
                summary={reportQ.isFetching ? 'Loading…' : `Page ${page} of ${totalPages}`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function WafRouteMap({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Waypoints size={22} />}
        title="Route Map"
        description="See which CDN platform and routing strategy serves each geo and network for the selected subdomain."
      />

      <div className={styles.scope}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Waypoints size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain. Proxy a record to see its route map."
        />
      ) : domain ? (
        <RouteMapBody key={domain} serviceId={serviceId} domain={domain} />
      ) : (
        <EmptyState
          icon={<Waypoints size={20} />}
          title="Select a subdomain"
          description="Pick a subdomain above to view its route map."
        />
      )}
    </Container>
  );
}

export default WafRouteMap;
